import { Heading, KeyValueList, Text } from "@smooth/design-system-components"
import { Bubble } from "../components/Conversation"
import { MultilineText, TerminalName } from "../components/parts"
import { DecisionRecord } from "../components/DecisionRecord"
import { isExpected, isUnsetItem } from "./comparison"
import type { BatchItem } from "./types"

type Props = {
  item: BatchItem
  terminals: Record<string, string>
}

/**
 * ケースを開いたときに出す、プロンプトの比較での結果（AIが選んだ対応・一致／不一致・本文・AIの判断）。
 * 戻り先（比較の結果）はヘッダーの「← 比較の結果に戻る」から
 */
export function BatchCaseResult({ item, terminals }: Props) {
  const run = item.run
  const metadata = run?.generationMetadata
  const expected = isExpected(item)
  const unset = isUnsetItem(item)

  return (
    <section
      aria-label="比較の結果"
      className="mb-4 flex flex-col gap-3 rounded-md border border-border-default p-4"
    >
      <Heading level={3}>比較の結果</Heading>
      <KeyValueList
        items={[
          {
            key: "AIが選んだ対応",
            value: item.observedTerminal ? (
              <TerminalName
                id={item.observedTerminal}
                terminals={terminals}
                mismatch={!unset && !expected}
              />
            ) : (
              "—"
            ),
          },
          {
            key: "結果",
            value: (
              <Text as="span" weight="bold" tone={expected || unset ? "default" : "error"}>
                {unset ? "—" : expected ? "一致" : "不一致"}
              </Text>
            ),
          },
        ]}
      />
      {metadata?.humanReviewRequired ? (
        <div className="flex flex-col gap-1">
          <Text weight="bold">人手対応・顧客向け本文なし</Text>
          <MultilineText>{metadata.humanReviewReason ?? ""}</MultilineText>
        </div>
      ) : run?.body ? (
        <div className="flex flex-col">
          <Bubble direction="outbound" meta="AIドラフト（未送信）" draft>
            {run.body}
          </Bubble>
        </div>
      ) : (
        <Text>{item.lastError ?? (run ? "本文なし" : "生成していません")}</Text>
      )}
      {run?.status === "completed" && (
        <details className="border-t border-border-default">
          <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
            AIの判断
          </summary>
          <DecisionRecord metadata={metadata} />
        </details>
      )}
    </section>
  )
}
